import React from 'react';
import { styled as styled2 } from '@compiled/react';
import { Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { AsyncSelect } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { layers, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import debounce from '@atlassian/jira-platform-debounce-promise/src/ui/debounced-promise/index.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useJsmPirEntitlementResource } from '@atlassian/jira-router-resources-jsm-pir-entitlement';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	fetchConfluenceSpace,
	fetchConfluencePage,
} from '../../../services/fetch-confluence/index.tsx';
import { usePIRConfiguration } from '../../../services/main.tsx';
import messages from './messages';
import type { DropDownOption } from './types';

const DEBOUNCE_TIME = 500;

export const PostIncidentReviewConfluenceSection = () => {
	const { formatMessage } = useIntl();
	const { cloudId } = useTenantContext();
	const { isEntitled: isPirEntitled } = useJsmPirEntitlementResource(cloudId);
	const [
		{ pirConfigurationData, defaultSpaceValue, defaultPageValue },
		{ setConfluenceSpace, setConfluencePage, clearConfluencePage },
	] = usePIRConfiguration();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'search',
		actionSubject: 'PostIncidentReviewConfluenceSection',
	});

	const { showFlag } = useFlagsService();

	const handleSpaceSearch = async (query = '') => {
		try {
			const response = await fetchConfluenceSpace(query, cloudId);
			fireOperationalAnalytics(analyticsEvent, 'handleConfluenceSpaceSearch succeeded');
			return response.map(({ name, key }) => ({
				label: name,
				value: key,
			}));
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			showFlag({
				type: 'error',
				title: messages.pirConfluenceErrorFlagConfluenceSpace,
			});
			fireErrorAnalytics({
				meta: {
					id: 'handleConfluenceSpaceSearch',
					packageName: 'jiraServicedeskIncidentManagementSettings',
					teamName: 'itsm-agni',
				},
				sendToPrivacyUnsafeSplunk: true,
				error,
			});
			return [];
		}
	};

	const handlePageSearch = async (query = '') => {
		if (pirConfigurationData?.selectedConfluenceSpaceId === undefined) {
			return [];
		}
		try {
			const response = await fetchConfluencePage(
				pirConfigurationData.selectedConfluenceSpaceId,
				cloudId,
				query,
			);
			fireOperationalAnalytics(analyticsEvent, 'handleConfluencePageSearch succeeded');
			return response.map(({ title, id }) => ({
				label: title,
				value: id,
			}));
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			showFlag({
				type: 'error',
				title: messages.pirConfluenceErrorFlagConfluencePage,
			});
			fireErrorAnalytics({
				meta: {
					id: 'handleConfluencePageSearch',
					packageName: 'jiraServicedeskIncidentManagementSettings',
					teamName: 'itsm-agni',
				},
				sendToPrivacyUnsafeSplunk: true,
				error,
			});
			return [];
		}
	};

	const handleSpaceSearchDebounce = debounce(handleSpaceSearch, DEBOUNCE_TIME);
	const handlePageSearchDebounce = debounce(handlePageSearch, DEBOUNCE_TIME);

	return (
		<>
			{ff('im_feature_enablement_gating') ? (
				<Heading level="h400">
					<Box xcss={!isPirEntitled && disabledTextStyle}>
						{formatMessage(messages.pirConfluenceSectionHeader)}
					</Box>
				</Heading>
			) : (
				<Heading level="h400">{formatMessage(messages.pirConfluenceSectionHeader)}</Heading>
			)}

			<SectionLabel isDisabled={!isPirEntitled} htmlFor="pir-confluence-space-description">
				{formatMessage(messages.pirConfluenceSpaceDescription)}
			</SectionLabel>
			<FieldWrapper>
				<Field name="space">
					{({ fieldProps }) => (
						<AsyncSelect
							{...fieldProps}
							inputId="pir-confluence-space-description"
							value={defaultSpaceValue}
							loadOptions={handleSpaceSearchDebounce}
							// @ts-expect-error - TS2322 - Type '({ value, label }: DropDownOption) => void' is not assignable to type '(value: { label: string; value: string; } | null, action: ActionMeta<{ label: string; value: string; }>) => void'.
							onChange={({ value, label }: DropDownOption) => {
								setConfluenceSpace(value, label);
							}}
							menuPortalTarget={document.body}
							menuPlacement="auto"
							styles={{
								menuPortal: (base) => ({
									...base,

									zIndex: layers.modal,
								}),
							}}
							isDisabled={!isPirEntitled}
						/>
					)}
				</Field>
			</FieldWrapper>

			<SectionLabel isDisabled={!isPirEntitled} htmlFor="pir-confluence-page-description">
				{formatMessage(messages.pirConfluencePageDescription)}
			</SectionLabel>
			<FieldWrapper>
				<Field name="page">
					{({ fieldProps }) => (
						<AsyncSelect
							{...fieldProps}
							inputId="pir-confluence-page-description"
							isClearable
							value={defaultPageValue}
							loadOptions={handlePageSearchDebounce}
							// @ts-expect-error - TS2322 - Type '(option: DropDownOption) => void' is not assignable to type '(value: { label: string; value: string; } | null, action: ActionMeta<{ label: string; value: string; }>) => void'.
							onChange={(option: DropDownOption) => {
								if (option) {
									setConfluencePage(option.value, option.label);
								} else {
									clearConfluencePage();
								}
							}}
							menuPortalTarget={document.body}
							menuPlacement="auto"
							styles={{
								menuPortal: (base) => ({
									...base,

									zIndex: layers.modal,
								}),
							}}
							isDisabled={!isPirEntitled}
						/>
					)}
				</Field>
			</FieldWrapper>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled2.div({
	width: '50%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 30}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionLabel = styled2.label<{ isDisabled?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isDisabled }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		isDisabled ? token('color.text.disabled', '#A5ADBA') : token('color.text.subtle', colors.N500),
	marginTop: token('space.300', '24px'),
	display: 'block',
});

const disabledTextStyle = xcss({ color: 'color.text.disabled' });
